import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ExchangeAccount } from '@/state/exchanges/exchangesSliceTypes';
import { EXCHANGES } from '@/lib/types/generalTypes';
import { accountsQuery } from '@/api/queries/accountsQuery';
import {
    selectAccountById,
    selectIsDemoAvailable,
    selectHasValidExchangesLength,
    selectAccountsNav,
    selectAccountsByIds,
    selectAccountsByExchange,
    selectAccountIdsByExchange,
    selectAccountIds,
    selectAccountNames,
    selectExchangeList,
    selectUnlockedAccounts,
    type SelectAccountIdsParams,
    type AccountNamesMap,
} from '@/api/selectors/accountSelectors';

export type { AccountNamesMap };

const useAccountsQuery = <T = ExchangeAccount[]>(select?: (accounts: ExchangeAccount[]) => T) => {
    return useQuery({
        ...accountsQuery,
        select,
    });
};

export const useAccountById = (id: number | string | null | undefined | false) => {
    const select = useCallback(
        (accounts: ExchangeAccount[]) => selectAccountById(accounts, id),
        [id],
    );
    return useAccountsQuery(select);
};

export const useIsDemoAvailable = () => useAccountsQuery(selectIsDemoAvailable);

export const useHasValidExchangesLength = () => useAccountsQuery(selectHasValidExchangesLength);

export const useAccountsNav = () => useAccountsQuery(selectAccountsNav);

export const useAccountsById = (accountIds: number[]) =>
    useAccountsQuery((accounts) => selectAccountsByIds(accounts, accountIds));

export const useAccountsByExchange = (selectedExchange: EXCHANGES | null | undefined) =>
    useAccountsQuery((accounts) => selectAccountsByExchange(accounts, selectedExchange));

export const useAccountIdsByExchange = () => useAccountsQuery(selectAccountIdsByExchange);

export const useAccountIds = (params: SelectAccountIdsParams) =>
    useAccountsQuery((accounts) => selectAccountIds(accounts, params));

export const useAccountNames = () => useAccountsQuery(selectAccountNames);

export const useExchangeList = () => useAccountsQuery(selectExchangeList);

export const useUnlockedAccounts = () => useAccountsQuery(selectUnlockedAccounts);

export default useAccountsQuery;
